import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CreatePost from './pages/CreatePost';
import ContentLibrary from './pages/ContentLibrary';
import ConnectedAccounts from './pages/ConnectedAccounts';
import AutoPost from './pages/AutoPost';
import IgamingAutoPost from './pages/IgamingAutoPost';
import ControlRoom from './pages/ControlRoom';
import UserManagement from './pages/UserManagement';
import ScheduleManager from './pages/ScheduleManager';
import SetupGuide from './pages/SetupGuide';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PromotionsManager from './pages/PromotionsManager';
import Layout from './components/Layout';

// Auth Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { TwoFASetup, TwoFAVerify } from './components/TwoFA';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, requires2FASetup, requires2FAVerify } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (requires2FAVerify) {
    return <TwoFAVerify />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requires2FASetup) {
    return <TwoFASetup />;
  }
  
  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/create" element={
        <ProtectedRoute>
          <Layout>
            <CreatePost />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/posts" element={
        <ProtectedRoute>
          <Layout>
            <ContentLibrary />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/accounts" element={
        <ProtectedRoute>
          <Layout>
            <ConnectedAccounts />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/auto-post" element={
        <ProtectedRoute>
          <Layout>
            <AutoPost />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/igaming" element={
        <ProtectedRoute>
          <Layout>
            <IgamingAutoPost />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/control-room" element={
        <ProtectedRoute>
          <Layout>
            <ControlRoom />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute>
          <Layout>
            <UserManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/schedules" element={
        <ProtectedRoute>
          <Layout>
            <ScheduleManager />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/promotions" element={
        <ProtectedRoute>
          <Layout>
            <PromotionsManager />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/setup-guide" element={
        <ProtectedRoute>
          <Layout>
            <SetupGuide />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Alias for connected-accounts (for OAuth callbacks) */}
      <Route path="/connected-accounts" element={
        <ProtectedRoute>
          <Layout>
            <ConnectedAccounts />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Public Legal Pages (required for TikTok app) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
